import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Get the timezone from environment variables
 * Falls back to 'Europe/Prague' if not set
 * @returns IANA timezone identifier
 */
export function getTimezone(): string {
  const timezone = import.meta.env.VITE_TIME_ZONE || (typeof window !== 'undefined' && window.ENV?.TIME_ZONE)
  return timezone || 'Europe/Prague'
}

export function secondsToHHMM(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  if (hours > 0) {
    return `${hours}h:${minutes.toString().padStart(2, '0')}m`
  }
  return `${minutes}m`
}

const zeroPad = (num: number) => String(num).padStart(2, '0')

export const secondsToHHM = (secs?: number) => {
  if (secs === undefined) return

  const hours = Math.floor(secs / 3600)
  const minutes = Math.floor((secs - hours * 3600) / 60)

  return `${zeroPad(hours)}:${zeroPad(minutes)}`
}

// Parse and validate dates
export const parseAndValidateDates = (startDateParam: string, endDateParam: string) => {
  const defaultStart = dayjs().toDate()
  const defaultEnd = dayjs().toDate()

  let startDate = startDateParam ? dayjs(startDateParam).toDate() : defaultStart
  let endDate = endDateParam ? dayjs(endDateParam).toDate() : defaultEnd

  // Ensure start date is before end date
  if (dayjs(startDate).isAfter(endDate)) {
    ;[startDate, endDate] = [endDate, startDate]
  }

  return { startDate, endDate }
}

const formatDateTime = (dateString?: string | null) => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    return dayjs(date).format('DD.MM.YYYY, HH:mm')
  } catch {
    return dateString
  }
}

const formatDateForUrl = (date: Date) => dayjs(date).format('YYYY-MM-DD')

const formatDateForTimestamp = (date: Date) => dayjs(date).format('YYYY-MM-DDTHH:mm:ssZ')

const dateSpanToArray = (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs) => {
  const span = endDate.diff(startDate, 'day')
  return Array.from({ length: span + 1 }, (_, i) => startDate.add(i, 'day').format('YYYY-MM-DD'))
}

export const toPragueLocaleString = (date: Date, options: Omit<Intl.DateTimeFormatOptions, 'timeZone'> = {}) => {
  return date.toLocaleString('cs-CZ', {
    timeZone: 'Europe/Prague',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  })
}

export const secondsToHumanReadable = (secs: number) => {
  const hours = Math.floor(secs / 3600)
  const minutes = Math.floor((secs - hours * 3600) / 60)
  const seconds = secs - hours * 3600 - minutes * 60

  if (hours > 0) return `${hours.toFixed(0)} hod ${minutes.toFixed(0)} min`
  if (minutes > 0) return `${minutes.toFixed(0)} min`
  return seconds.toFixed(0)
}
/**
 * Format date time for URL
 * @param date - Date to format
 * @returns Formatted date time for URL
 * Example: 2025-01-01T00:00:00Z
 */
export const formatDateTimeForUrl = (date: Date) => dayjs(date).utc().format('YYYY-MM-DDTHH:mm') + 'Z'

export function formatTime(timeInput: string, timezone: string): string {
  const timeStringMatch = timeInput.match(/^(\d{1,2}):(\d{2})$/)
  if (timeStringMatch) {
    const hours = parseInt(timeStringMatch[1], 10)
    const minutes = parseInt(timeStringMatch[2], 10)
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
  }
  return dayjs.utc(timeInput).tz(timezone).format('HH:mm')
}

export function formatInterval(interval: { starting_from: string; ending_at: string }, timezone: string): string {
  const start = formatTime(interval.starting_from, timezone)
  const end = formatTime(interval.ending_at, timezone)
  return `${start} - ${end}`
}

/** @deprecated Use formatTime instead */
export const formatTimeWithoutDST = formatTime
/** @deprecated Use formatInterval instead */
export const formatIntervalWithoutDST = formatInterval

export { formatDateTime, formatDateForUrl, formatDateForTimestamp, dateSpanToArray }
